import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import useApiData from "../hooks/useApiData";

const fallbackFaqPage = {
  hero: {
    badge: "Support",
    title: "Frequently Asked Questions",
    description:
      "Find answers to common questions about DVX programs and memberships",
  },
  cta: {
    title: "Still Have Questions?",
    description:
      "Can't find what you're looking for? Our support team is here to help.",
    button_text: "Contact Support",
    button_href: "/contact",
  },
  faqs: [],
};

export default function FAQ() {
  const {
    data: pageData,
    loading,
    error,
  } = useApiData("/faqs", fallbackFaqPage);

  // Safe access
  const faqs = pageData?.faqs || [];

  return (
    <div className="min-h-screen bg-[var(--dvx-bg)] pt-24 transition-colors duration-300">
      {/* HERO */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-[var(--dvx-primary)] font-oswald text-sm uppercase tracking-widest">
              {pageData?.hero?.badge || "FAQ"}
            </span>

            <h1 className="mt-4 font-oswald text-4xl md:text-5xl lg:text-6xl font-bold text-[var(--dvx-text)] uppercase tracking-tight">
              {pageData?.hero?.title || "Questions & Answers"}
            </h1>

            <p className="mt-4 text-[var(--dvx-text-muted)] font-manrope text-lg max-w-2xl mx-auto">
              {pageData?.hero?.description ||
                "Find answers to common questions."}
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ LIST */}
      <section className="pb-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* LOADING */}
          {loading && (
            <div className="flex justify-center py-20">
              <div className="font-oswald text-xl text-[var(--dvx-text)] uppercase tracking-widest animate-pulse">
                Loading FAQs...
              </div>
            </div>
          )}

          {/* ERROR */}
          {!loading && error && (
            <div className="text-center py-20">
              <p className="text-red-500 font-oswald uppercase">
                Failed to load FAQs.
              </p>
              <p className="text-sm text-[var(--dvx-text-muted)] mt-2">
                Please try again later.
              </p>
            </div>
          )}

          {/* EMPTY STATE */}
          {!loading && !error && faqs.length === 0 && (
            <div className="text-center py-20">
              <h3 className="font-oswald text-2xl text-[var(--dvx-text)] uppercase">
                No FAQs Found
              </h3>
              <p className="text-[var(--dvx-text-muted)] mt-2">
                We currently don’t have any questions available.
              </p>
            </div>
          )}

          {/* FAQ ACCORDION */}
          {!loading && !error && faqs.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Accordion type="single" collapsible className="space-y-4">
                {faqs.map((faq, index) => (
                  <AccordionItem
                    key={`${faq?.question || index}-${index}`}
                    value={`item-${index}`}
                    className="border border-[var(--dvx-border)] bg-[var(--dvx-surface)] px-6 rounded-2xl shadow-sm"
                  >
                    <AccordionTrigger className="font-oswald text-[var(--dvx-text)] text-left uppercase tracking-wide hover:text-[var(--dvx-primary)] py-6 hover:no-underline">
                      {faq?.question || "No question provided"}
                    </AccordionTrigger>

                    <AccordionContent className="text-[var(--dvx-text-muted)] font-manrope pb-6 leading-relaxed">
                      {faq?.answer || "No answer available."}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[var(--dvx-surface)] transition-colors duration-300">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-oswald text-2xl md:text-3xl font-bold text-[var(--dvx-text)] uppercase mb-4">
              {pageData?.cta?.title || "Need Help?"}
            </h2>

            <p className="text-[var(--dvx-text-muted)] font-manrope mb-6">
              {pageData?.cta?.description || "Contact our support team."}
            </p>

            <a
              href={pageData?.cta?.button_href || "/contact"}
              className="inline-block px-8 py-4 bg-gradient-to-r from-[var(--dvx-primary)] to-[var(--dvx-primary-dark)] text-white font-oswald font-bold uppercase tracking-widest hover:scale-105 transition-transform"
            >
              {pageData?.cta?.button_text || "Contact"}
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
